// based on https://github.com/lucacasonato/esbuild_deno_loader/blob/0.10.3/src/shared.ts
// Copyright 2021 Luca Casonato. All rights reserved. MIT license.

import { createErr, createOk, Result } from "./deps/option-t.ts";
import { ALL, Range, tryParseRange } from "./deps/semver.ts";

export interface NpmSpecifier {
  name: string;
  range: Range;
  /** raw version string */
  tag?: string;
  entryPoint: "." | `./${string}`;
}
export interface NotNpmProtocolError {
  name: "NotNpmProtocolError";
  specifier: URL;
}
export interface OnlyScopeProvidedError {
  name: "OnlyScopeProvidedError";
  specifier: URL;
}
const onlyScopeProvidedError = (specifier: URL): OnlyScopeProvidedError => ({
  name: "OnlyScopeProvidedError",
  specifier,
});
export interface PackageNotFoundError {
  name: "PackageNotFoundError";
  specifier: URL;
}
const packageNotFoundError = (specifier: URL): PackageNotFoundError => ({
  name: "PackageNotFoundError",
  specifier,
});
export interface InvalidNpmSpecifierError {
  name: "InvalidNpmSpecifierError";
  specifier: URL;
}

export const parseNpmSpecifier = (
  specifier: URL,
): Result<
  NpmSpecifier,
  NotNpmProtocolError | OnlyScopeProvidedError | PackageNotFoundError
> => {
  if (specifier.protocol !== "npm:") {
    return createErr({ name: "NotNpmProtocolError", specifier });
  }
  const path = specifier.pathname;
  const startIndex = path[0] === "/" ? 1 : 0;
  let pathStartIndex;
  let versionStartIndex;
  if (path[startIndex] === "@") {
    const firstSlash = path.indexOf("/", startIndex);
    if (firstSlash === -1) return createErr(onlyScopeProvidedError(specifier));
    pathStartIndex = path.indexOf("/", firstSlash + 1);
    versionStartIndex = path.indexOf("@", firstSlash + 1);
  } else {
    pathStartIndex = path.indexOf("/", startIndex);
    versionStartIndex = path.indexOf("@", startIndex);
  }

  if (pathStartIndex === -1) pathStartIndex = path.length;
  if (versionStartIndex === -1) versionStartIndex = path.length;

  versionStartIndex = Math.min(versionStartIndex, pathStartIndex);

  if (startIndex === versionStartIndex) {
    return createErr(packageNotFoundError(specifier));
  }

  const name = path.slice(startIndex, versionStartIndex);
  const tag = decodeURIComponent(
    path.slice(versionStartIndex + 1, pathStartIndex),
  );
  const range = tag ? tryParseRange(tag) ?? [[ALL]] : [[ALL]];
  const rawEntryPoint = path.slice(pathStartIndex + 1);
  const entryPoint = rawEntryPoint
    ? `./${rawEntryPoint}` as const
    : "." as const;
  const npm: NpmSpecifier = { name, range, entryPoint };
  if (tag) npm.tag = tag;

  return createOk(npm);
};

export interface JsrSpecifier extends NpmSpecifier {}
export interface NotJsrProtocolError {
  name: "NotJsrProtocolError";
  specifier: URL;
}
export interface ScopeNotFoundError {
  name: "ScopeNotFoundError";
  specifier: URL;
}

export const parseJsrSpecifier = (
  specifier: URL,
): Result<
  JsrSpecifier,
  NotJsrProtocolError | ScopeNotFoundError | PackageNotFoundError
> => {
  if (specifier.protocol !== "jsr:") {
    return createErr({ name: "NotJsrProtocolError", specifier });
  }
  const path = specifier.pathname;
  const startIndex = path[0] === "/" ? 1 : 0;
  if (path[startIndex] !== "@") {
    return createErr({ name: "ScopeNotFoundError", specifier });
  }
  const firstSlash = path.indexOf("/", startIndex);
  if (firstSlash === -1) return createErr(packageNotFoundError(specifier));
  let pathStartIndex = path.indexOf("/", firstSlash + 1);
  let versionStartIndex = path.indexOf("@", firstSlash + 1);

  if (pathStartIndex === -1) pathStartIndex = path.length;
  if (versionStartIndex === -1) versionStartIndex = path.length;

  versionStartIndex = Math.min(versionStartIndex, pathStartIndex);
  const name = path.slice(startIndex, versionStartIndex);
  const tag = decodeURIComponent(
    path.slice(versionStartIndex + 1, pathStartIndex),
  );
  const range = tag ? tryParseRange(tag) ?? [[ALL]] : [[ALL]];
  const rawEntryPoint = path.slice(pathStartIndex + 1);
  const entryPoint = rawEntryPoint
    ? `./${rawEntryPoint}` as const
    : "." as const;
  const jsr: JsrSpecifier = { name, range, entryPoint };
  if (tag) jsr.tag = tag;

  return createOk(jsr);
};

// denoCacheMetadata={"headers":{"etag":"W/\"7096a356125f15fcd8099a48fe1c53a234cd36f68e8c4e646a779930849ed3d4\"","content-security-policy":"default-src 'none'; style-src 'unsafe-inline'; sandbox","x-served-by":"cache-iad-kcgs7200100-IAD","x-timer":"S1731060601.757500,VS0,VE2","cross-origin-resource-policy":"cross-origin","expires":"Fri, 08 Nov 2024 10:15:00 GMT","x-xss-protection":"1; mode=block","date":"Fri, 08 Nov 2024 10:10:00 GMT","accept-ranges":"bytes","via":"1.1 varnish","cache-control":"max-age=300","x-frame-options":"deny","strict-transport-security":"max-age=31536000","x-cache":"HIT","x-cache-hits":"0","x-content-type-options":"nosniff","vary":"Authorization,Accept-Encoding,Origin","x-fastly-request-id":"809e4b0b9050569006337ec5cb0ce7d8bd22680f","content-type":"text/plain; charset=utf-8","access-control-allow-origin":"*","x-github-request-id":"A717:17D8CE:1CFC82:1FB79A:672DDE07","source-age":"90"},"url":"https://raw.githubusercontent.com/takker99/ScrapJupyter/v1.7.4/specifiers.ts","time":1731060600}